import React, { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
} from 'lucide-react';

import { usePdfTab } from '../../hooks/usePdfTab';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';

/** The generated client's non-throwing result for a `responseType: 'blob'` call. */
export interface PayrollPdfResult {
  data?: unknown;
  error?: unknown;
}

interface PayrollExportMenuProps {
  /** Trigger text, e.g. "Export" or "Export All". */
  label: string;
  disabled?: boolean;
  className?: string;
  /** Downloads the workbook and reports its own errors. */
  onExportExcel: () => Promise<void>;
  /** Fetches the same rows as a PDF blob. */
  fetchPdf: () => Promise<PayrollPdfResult>;
}

const PDF_FAILED = 'Failed to export PDF. Please try again.';

/**
 * Payroll's Export dropdown: Excel downloads, PDF opens in a new tab. The
 * menu stays disabled while either runs, so one click makes one file.
 */
export const PayrollExportMenu: React.FC<PayrollExportMenuProps> = ({
  label,
  disabled = false,
  className,
  onExportExcel,
  fetchPdf,
}) => {
  const openPdfTab = usePdfTab();
  const toast = useToast();
  const [isBusy, setIsBusy] = useState(false);

  const handleExcel = async () => {
    setIsBusy(true);
    try {
      await onExportExcel();
    } finally {
      setIsBusy(false);
    }
  };

  const handlePdf = async () => {
    // Opened inside the click, before any await, so popup blockers allow it.
    const tab = openPdfTab();
    if (!tab) return;
    setIsBusy(true);
    try {
      const response = await fetchPdf();
      if (response.error !== undefined || !response.data) throw response;
      tab.setBlob(
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data as BlobPart], { type: 'application/pdf' })
      );
    } catch (error) {
      // An over-cap 422 explains how to narrow the range; a 5xx stays generic.
      console.error('Failed to export payroll PDF:', error);
      tab.close();
      toast.error(await getUserFacingApiMessageAsync(error, PDF_FAILED));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <DropdownMenu>
      {/* Disabled on the Radix trigger, not just the button: Radix opens on
          pointerdown, which a disabled child button does not stop. */}
      <DropdownMenuTrigger asChild disabled={disabled || isBusy}>
        <Button variant='outline' size='sm' className={className}>
          {isBusy ? (
            <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
          ) : (
            <Download className='h-4 w-4' aria-hidden='true' />
          )}
          {label}
          <ChevronDown className='h-4 w-4' aria-hidden='true' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem onClick={() => void handleExcel()}>
          <FileSpreadsheet aria-hidden='true' />
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void handlePdf()}>
          <FileText aria-hidden='true' />
          PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
